import { useState } from 'react';
import { useToast } from '@/components/layout/Toast';
import { SurveyLanguage } from '@/graphql/generated';
import { useAuthHeaders } from '@/hooks/useAuthHeaders';
import { useTranslation } from '@/hooks/useTranslation';

export interface UseExportFileProps {
  path: string;
  fileName: string;
  errorToastMessage: string;
  exportToLanguage?: SurveyLanguage;
}

export const enum ApiDownloadRoutes {
  GapAnalysisExport = '/report/:reportId/export',
  IndicatorsExport = '/indicators/:enterpriseId/export',
  ReferentialExport = '/referential/:referentialId/export',
  SurveyExport = '/survey/:surveyId/export/survey',
  FileUploadExport = '/file-upload/:enterpriseId/download/:fileUploadId',
}

export const useDownloadFile = ({
  path,
  fileName,
  errorToastMessage,
  exportToLanguage,
}: UseExportFileProps) => {
  const { i18n } = useTranslation();
  const toast = useToast();
  const getHeaders = useAuthHeaders();
  const [loading, setLoading] = useState(false);

  const language: SurveyLanguage =
    exportToLanguage || (i18n.language as SurveyLanguage) || SurveyLanguage.Fr;

  const downloadFile = async () => {
    try {
      const authHeaders = await getHeaders();
      if (!authHeaders.authorization) {
        return;
      }

      if (!process.env.API_URL) {
        toast.openToastWithError(errorToastMessage);
        return;
      }

      const apiUrl = process.env.API_URL;
      setLoading(true);

      const result = await fetch(`${apiUrl}${path}`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          ...authHeaders,
          'accept-language': language,
        },
      });

      if (!result.ok) {
        throw Error(result.statusText);
      }

      const blob = await result.blob();
      if (blob != null) {
        const downloadUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = downloadUrl;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        a.remove();
      }
    } catch (error) {
      if (error instanceof Error) {
        toast.openToastWithError(error.message);
      }
    } finally {
      setLoading(false);
    }
  };

  return { downloadFile, loading };
};
